import React, { useContext } from 'react';
import { ConfigContext } from '../context/ConfigContext';
import { useAuthContext } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import SessionInfo from './SessionInfo';
import { 
  Users, 
  CreditCard, 
  Heart, 
  FileText, 
  Calendar, 
  Bell, 
  LogOut,
  Home,
  Settings
} from 'lucide-react';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = React.useState(window.innerWidth >= 768);
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthContext();
  const currentUser = user || {};

  const menuItemsAdmin = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    { icon: Users, label: 'Gestión de Socios', path: '/members' },
    { icon: CreditCard, label: 'Pagos de Membresía', path: '/payments' },
    { icon: Heart, label: 'Gestión de Donaciones', path: '/donations' },
    { icon: FileText, label: 'Centro de Reportes', path: '/reports' },
    { icon: Calendar, label: 'Eventos de Cumpleaños', path: '/events' },
    { icon: Bell, label: 'Configuración de Recordatorios', path: '/notifications' },
    { icon: Settings, label: 'Configuración del Sistema', path: '/settings' }
  ];
  const menuItemsUser = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    { icon: CreditCard, label: 'Mis Pagos', path: '/payments' },
    { icon: Heart, label: 'Mis Donaciones', path: '/donations' },
    { icon: Calendar, label: 'Mis Eventos', path: '/events' }
  ];
  const menuItems = currentUser.role === 'admin' ? menuItemsAdmin : menuItemsUser;

  const handleLogout = () => {
    logout();
  };

  const { config } = useContext(ConfigContext);
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="flex">
        {/* Botón hamburguesa solo en móvil */}
        <button
          className="fixed top-4 left-4 z-50 p-2 rounded-xl bg-white shadow-lg md:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-menu"><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
        </button>
        {/* Sidebar */}
        <motion.div 
          className={`w-64 bg-white/90 backdrop-blur-xl border-r border-gray-200/50 shadow-xl min-h-screen fixed top-0 left-0 z-40 h-full md:static ${sidebarOpen ? 'block' : 'hidden'} md:block`}
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="p-6">
            {/* Botón cerrar sidebar en móvil */}
            <button
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 md:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <motion.div 
              className="flex items-center gap-3 mb-8"
              whileHover={{ scale: 1.02 }}
            >
              {config.loginLogo ? (
                <img src={config.loginLogo} alt="Logo" className="w-8 h-8 sm:w-10 sm:h-10 object-contain rounded-xl" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  {config.organizationName || 'BeneficiaHub'}
                </h1>
                <p className="text-xs text-gray-500">{config.loginSubtitle || 'Sistema de Gestión de Beneficencia'}</p>
              </div>
            </motion.div>

            <nav className="space-y-2">
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <motion.button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                      isActive 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{item.label}</span>
                  </motion.button>
                );
              })}
            </nav>
          </div>

          <div className="absolute bottom-6 left-6 right-6">
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {currentUser.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {currentUser.name || 'Usuario'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {currentUser.email || 'user@example.com'}
                  </p>
                </div>
              </div>
            </div>

            <motion.button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl font-medium transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Cerrar Sesión</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="p-2 sm:p-4 md:p-8">
            <Outlet />
          </div>
        </div>
      </div>
      <SessionInfo />
    </div>
  );
};

export default Layout;